/**
 * History — plan changes and email changes, the two audit trails the upstream
 * lookup already returns. Read-only.
 */
import type { UserAccount } from "../../../../../shared/index";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, EmptyState } from "./parts";

/**
 * Upstream prefixes every reason it writes with "Admin Master Console - ", so
 * the column reads as one repeated phrase. The raw value stays in the tooltip.
 */
const CONSOLE_PREFIX = /^Admin Master Console\s*-\s*/i;
import { EmailTag } from "./email-tag";
import { formatDate } from "./format";

export function HistoryTab({ account }: { account: UserAccount }) {
  // Same colours the Billing tab uses, so an address is recognisable across
  // both tabs — current address first, matching `knownEmails`.
  const allEmails = [
    account.email,
    ...account.emailHistory
      .filter((e) => e.email.toLowerCase() !== account.email.toLowerCase())
      .map((e) => e.email),
  ];

  return (
    <div className="space-y-4">
      <Card title="Plan history" description={`${account.planHistory.length} entries`}>
        {account.planHistory.length === 0 ? (
          <EmptyState>No plan changes recorded.</EmptyState>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plan</TableHead>
                <TableHead>From</TableHead>
                <TableHead>To</TableHead>
                <TableHead>Reason</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {account.planHistory.map((entry, i) => (
                <TableRow key={`${entry.productPlanName}-${entry.startDate}-${i}`}>
                  <TableCell className="font-medium">
                    {entry.productPlanName}
                  </TableCell>
                  <TableCell>{formatDate(entry.startDate)}</TableCell>
                  <TableCell>{formatDate(entry.endDate)}</TableCell>
                  <TableCell className="text-muted-foreground" title={entry.reason}>
                    {entry.reason?.replace(CONSOLE_PREFIX, "") || "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Card
        title="Email history"
        description="Every address this account has used"
      >
        {account.emailHistory.length === 0 ? (
          <EmptyState>No email changes recorded.</EmptyState>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {account.emailHistory.map((entry, i) => (
                <TableRow key={`${entry.email}-${i}`}>
                  <TableCell className="font-medium">
                    <EmailTag
                      email={entry.email}
                      all={allEmails}
                      current={entry.isCurrent}
                    />
                  </TableCell>
                  <TableCell>{entry.status || "—"}</TableCell>
                  <TableCell>{formatDate(entry.creationDate)}</TableCell>
                  <TableCell>{formatDate(entry.updatedDate)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
